import React, { useEffect, useState } from "react";
import { Calendar, Clock, TrendingUp, Users, Package, DollarSign } from "lucide-react";
import Axios from "../../../services/AxiosInterceptor";
import { FaRupeeSign } from "react-icons/fa";


const Dashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  // State variables for the calculated stats
  const [totalBookings, setTotalBookings] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalSeats, setTotalSeats] = useState(0);

  console.log("totalBookings",bookings)

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const Role = localStorage.getItem("role"); 
        if (!Role) {
          console.error("No User ID found in localStorage");
          return;
        }

        const response = await Axios.post("api/get_booking_data", { role: Role });
        const reverse = response.data.data.reverse();
        setBookings(reverse);

        // Calculate total stats based on the fetched bookings data
        const totalAmount = reverse.reduce((sum, booking) => sum + Number(booking?.amountPaid), 0);
        setTotalBookings(totalAmount);

        // Count total unique customers based on userId
        const uniqueCustomers = new Set(reverse.map((booking) => booking.userId));
        setTotalCustomers(uniqueCustomers.size);

        // Calculate total seats booked
        const totalBookedSeats = reverse.reduce((sum, booking) => sum + booking.seatsBooked, 0);
        setTotalSeats(totalBookedSeats);
      } catch (error) {
        console.error("Error fetching bookings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const stats = [
    { title: "Total Bookings", value: `Rs ${totalBookings}`, icon: FaRupeeSign, trend: "+12.5%" },
    { title: "Active Orders", value: `${totalSeats}`, icon: Package, trend: "+5%" },
    { title: "Customers", value: `${totalCustomers}`, icon: Users, trend: "+8.2%" },
    { title: "Growth", value: "32%", icon: TrendingUp, trend: "+3.1%" },
  ];

  return (
    <div className="min-h-screen p-6 bg-background">
      {/* Welcome Header */}
      <div className="mb-8">
        <div className="bg-gradient-gold rounded-xl p-8 shadow-[var(--shadow-gold)] animate-fade-in">
          <h1 className="mb-2 text-4xl font-bold text-primary-foreground">
            Welcome Back, Ajay! 👋
          </h1>
          <p className="text-lg text-primary-foreground/90">
            Here's what's happening with your business today
          </p>
        </div>
      </div>

      {/* Date and Time Cards */}
      <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2">
        {/* Today's Date Card */}
        <div
          className="bg-card border-border hover:shadow-[var(--shadow-gold)] transition-all duration-300 animate-fade-in-up p-6 rounded-lg"
        >
          <div className="flex items-center gap-2 text-primary">
            <Calendar className="w-5 h-5" />
            <h3 className="text-xl font-semibold">Today's Date</h3>
          </div>
          <p className="mt-4 text-2xl font-semibold text-foreground">
            {formatDate(currentTime)}
          </p>
        </div>

        {/* Current Time Card */}
        <div
          className="bg-card border-border hover:shadow-[var(--shadow-gold)] transition-all duration-300 animate-fade-in-up p-6 rounded-lg"
          style={{ animationDelay: "0.1s" }}
        >
          <div className="flex items-center gap-2 text-primary">
            <Clock className="w-5 h-5" />
            <h3 className="text-xl font-semibold">Current Time</h3>
          </div>
          <p className="mt-4 font-mono text-2xl font-semibold text-foreground">
            {formatTime(currentTime)}
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => (
          <div
            key={stat.title}
            className="bg-card border-border hover:shadow-[var(--shadow-gold)] hover:scale-105 transition-all duration-300 animate-fade-in-up group p-6 rounded-lg"
            style={{ animationDelay: `${0.2 + index * 0.1}s` }}
          >
            <div className="flex flex-row items-center justify-between pb-2">
              <h4 className="text-sm font-medium text-muted-foreground">{stat.title}</h4>
              <stat.icon className="w-5 h-5 text-primary group-hover:animate-glow" />
            </div>
            <div className="mb-1 text-3xl font-bold text-foreground">{stat.value}</div>
            <p className="text-xs font-semibold text-primary">{stat.trend} from last month</p>
          </div>
        ))}
      </div>

      {/* Motivational Message */}
      <div
        className="p-6 mt-8 rounded-lg bg-gradient-to-r from-card to-secondary border-border animate-fade-in-up"
        style={{ animationDelay: "0.6s" }}
      >
        <p className="text-lg italic text-center text-foreground">
          "Success is not the key to happiness. Happiness is the key to success. If you love what you are doing, you will be successful." 🌟
        </p>
      </div>
    </div>
  );
};

export default Dashboard;
